import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import type { PartnerDebtDraft } from '../types/coffeeInventory';

function escapeHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMoney(n: number): string {
  return Number(n || 0).toLocaleString('vi-VN');
}

/** Đợi layout + font paint trước khi html2canvas chụp (tránh PDF trắng). */
function waitForRender(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 80);
      });
    });
  });
}

export async function downloadPartnerDebtInvoicePdf(params: {
  draft: PartnerDebtDraft;
  invoiceDate: Date;
  storeDisplayName: string;
}): Promise<void> {
  const { draft, invoiceDate, storeDisplayName } = params;
  const issueRef = draft.issueDate || draft.createdAt;
  const issueDateStr = issueRef ? dayjs(issueRef).format('DD/MM/YYYY') : '—';
  const invDateStr = dayjs(invoiceDate).format('DD/MM/YYYY');
  const discountRate = Number(draft.discountRate || 0);

  let subtotal = 0;
  const rowsHtml = (draft.lines || [])
    .map((line, idx) => {
      const kg = Number(line.quantity || 0) / 1000;
      const price = Number(line.unitPricePerKg || 0);
      const amt = kg * price;
      subtotal += amt;
      return `<tr>
        <td style="text-align:center;border:1px solid #ccc;padding:6px;">${idx + 1}</td>
        <td style="border:1px solid #ccc;padding:6px;">${escapeHtml(line.coffeeTypeName || '')}</td>
        <td style="text-align:right;border:1px solid #ccc;padding:6px;">${kg.toLocaleString('vi-VN', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        })}</td>
        <td style="text-align:right;border:1px solid #ccc;padding:6px;">${formatMoney(price)}</td>
        <td style="text-align:right;border:1px solid #ccc;padding:6px;">${formatMoney(amt)}</td>
      </tr>`;
    })
    .join('');

  const afterDisc = subtotal * (1 - discountRate / 100);

  const html = `
  <div id="partner-debt-invoice-root" style="font-family: Arial, 'Segoe UI', sans-serif; font-size: 11px; color: #111111; padding: 16px; width: 190mm; max-width: 720px; box-sizing:border-box; background:#ffffff;">
    <h2 style="text-align:center; margin: 0 0 6px; font-size:16px; color:#111;">PHIẾU / HÓA ĐƠN GHI NHẬN CÔNG NỢ ĐỐI TÁC</h2>
    <p style="text-align:center; margin: 0 0 14px; color:#444; font-size:10px;">Bản in kèm xuất HĐ — 8AM Coffee Admin</p>
    <table style="width:100%; margin-bottom:12px;">
      <tr><td style="padding:3px 0; width:38%;"><b>Số phiếu</b></td><td>${escapeHtml(draft.id || '—')}</td></tr>
      <tr><td style="padding:3px 0;"><b>Đối tác</b></td><td>${escapeHtml(draft.partnerName || '')}</td></tr>
      <tr><td style="padding:3px 0;"><b>Ngày nhập phiếu</b></td><td>${issueDateStr}</td></tr>
      <tr><td style="padding:3px 0;"><b>Ngày giao dịch / xuất HĐ</b></td><td>${invDateStr}</td></tr>
      <tr><td style="padding:3px 0;"><b>Cơ sở xuất</b></td><td>${escapeHtml(storeDisplayName || '—')}</td></tr>
      <tr><td style="padding:3px 0;"><b>Chiết khấu</b></td><td>${discountRate.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%</td></tr>
    </table>
    <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">
      <thead>
        <tr style="background:#eeeeee;">
          <th style="border:1px solid #ccc;padding:6px;">STT</th>
          <th style="border:1px solid #ccc;padding:6px;">Hàng hóa</th>
          <th style="border:1px solid #ccc;padding:6px;">SL (kg)</th>
          <th style="border:1px solid #ccc;padding:6px;">Đơn giá/kg (đ)</th>
          <th style="border:1px solid #ccc;padding:6px;">Thành tiền (đ)</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <p style="text-align:right; margin:4px 0;"><b>Tạm tính:</b> ${formatMoney(subtotal)} đ</p>
    <p style="text-align:right; margin:4px 0;"><b>Sau chiết khấu:</b> ${formatMoney(afterDisc)} đ</p>
    ${draft.notes ? `<p style="margin-top:10px;"><b>Ghi chú:</b> ${escapeHtml(draft.notes)}</p>` : ''}
    <p style="margin-top:28px; color:#666; font-size:10px;">Người lập phiếu / Đối tác ký xác nhận: …………………………………………</p>
  </div>`;

  const wrapper = document.createElement('div');
  wrapper.setAttribute('data-partner-debt-pdf', '1');
  wrapper.innerHTML = html.trim();

  // html2canvas coi opacity rất thấp / z-index âm như nội dung trong suốt → PDF trắng (~3KB).
  Object.assign(wrapper.style, {
    position: 'fixed',
    left: '-16000px',
    top: '0',
    width: '794px',
    backgroundColor: '#ffffff',
    color: '#111111',
    opacity: '1',
    zIndex: '2147483646',
    pointerEvents: 'none',
    overflow: 'visible',
  });

  document.body.appendChild(wrapper);

  const root = (wrapper.firstElementChild as HTMLElement | null) || wrapper;

  const safeName = (draft.partnerName || 'phieu')
    .replace(/[\\/:*?"<>|]+/g, '')
    .trim()
    .replace(/\s+/g, '_');
  const fname = `HoaDonCongNo_${safeName || 'phieu'}_${dayjs(invoiceDate).format('DDMMYYYY_HHmm')}.pdf`;

  try {
    await waitForRender();
    await html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: fname,
        image: { type: 'jpeg', quality: 0.92 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          windowWidth: root.scrollWidth || 794,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(root)
      .save();
  } finally {
    document.body.removeChild(wrapper);
  }
}
